export type HeroVariant = 'intervention' | 'automation' | 'category'

export const heroes: Record<HeroVariant, { headline: string; body: string }> = {
  intervention: {
    headline: 'Know what needs intervention before it becomes an opening-day problem.',
    body: 'KeyTurn is hotel project intelligence that works in the background - surfacing risk, turning meetings and workbooks into outcomes, and giving every person their next turn.',
  },
  automation: {
    headline: 'Focus on opening the hotel. KeyTurn will manage the moving parts.',
    body: 'From live Health and relevant alerts to meeting outcomes and implementation workbooks, KeyTurn keeps the plan current without making the team live inside a project tool.',
  },
  category: {
    headline: 'The intelligence layer for hotel projects.',
    body: 'KeyTurn connects the plan, the people, the meetings and the source documents - then shows you what matters now and what every change means for the date that cannot move.',
  },
}

/** Breadth, stated once in the hero without diluting the opening-day promise. */
export const heroScope = ['Openings', 'Takeovers', 'Rebrands', 'Group rollouts']

export const proofPoints = [
  'Purpose-built for hotel projects with a fixed date',
  'Every person sees their next turn',
  'Every change shows its consequence',
  'Every project strengthens the next',
]

/** The KeyTurn application. Same tab: this is our product, not a third party. */
export const signInUrl = 'https://app.keyturn.online'

export const navLinks = [
  { label: 'Why KeyTurn', href: '#why' },
  { label: 'Attention', href: '#attention' },
  { label: 'Meetings', href: '#meetings' },
  { label: 'Workbooks', href: '#workbooks' },
  { label: 'Projects', href: '#projects' },
]

/**
 * The core repositioning: a project tool is another job. Left is what a register
 * does, right is what KeyTurn does instead.
 */
export const toolContrast = [
  {
    tool: 'Waits for someone to update it',
    keyturn: 'Watches the plan itself and reports what moved',
  },
  {
    tool: 'Tells you what changed',
    keyturn: 'Tells you what the change means for the date',
  },
  {
    tool: 'Notifies everyone about everything',
    keyturn: 'Notifies only the people a change now affects',
  },
  {
    tool: 'Turns every meeting into homework',
    keyturn: 'Turns the meeting into applied outcomes',
  },
  {
    tool: 'Starts each project from a blank template',
    keyturn: 'Starts from what your last project actually taught you',
  },
]

export const projectTypes = [
  {
    name: 'New openings',
    detail:
      'The core. Pre-opening critical path from anchor milestones through commissioning, snagging and handover.',
    core: true,
  },
  {
    name: 'Takeovers and transitions',
    detail:
      'Day-one continuity across systems, licences, contracts and people, with a live date you cannot move.',
    core: false,
  },
  {
    name: 'Rebrands and repositionings',
    detail:
      'Brand standards, signage, collateral, distribution and systems coordinated while the hotel keeps trading.',
    core: false,
  },
  {
    name: 'Group project rollouts',
    detail:
      'One programme run across many properties, with portfolio Health showing which sites need intervention.',
    core: false,
  },
  {
    name: 'Refurbishments',
    detail:
      'Phased works sequenced against trading, with consequences visible before a key date moves.',
    core: false,
  },
]

export const openings = [
  {
    name: 'The Wren, Shoreditch',
    phase: 'Fit-out',
    runway: '84 days to opening',
    score: 82,
    previous: 79,
    note: 'Tracking to plan. Nothing needs you this week.',
  },
  {
    name: 'Marchmont House',
    phase: 'Commissioning',
    runway: '41 days to opening',
    score: 58,
    previous: 65,
    note: 'Room type names unconfirmed - holding distribution setup.',
  },
  {
    name: 'Ardmore Bay Resort',
    phase: 'Snagging',
    runway: '26 days to opening',
    score: 41,
    previous: 50,
    note: 'Liquor licence now expected nine days after opening.',
  },
  {
    name: 'Kestrel Quay',
    phase: 'Handover',
    runway: '112 days to opening',
    score: 74,
    previous: 74,
    note: 'Steady. Two questions waiting on the brand team.',
  },
]

export const healthEngine = {
  headline: 'One number that ends the search for the problem.',
  lead: 'A hotel project generates hundreds of tasks and thousands of small movements. Almost none of them need you. KeyTurn Health reads all of it continuously and returns the handful that do.',
  support:
    'It is the engine underneath everything else on this page. My Turn, the alerts, the meeting loop and the recommendations all inherit their priority from it, which is why the work that reaches a person is the work that matters.',
  inputs: [
    'Blockers',
    'Timing and runway',
    'Ownership',
    'Stale activity',
    'Dependencies',
    'Opening significance',
  ],
}

export const healthBands = [
  {
    label: 'Healthy',
    range: '70-100',
    score: 82,
    previous: 79,
    blurb: 'Tracking to plan. It stays in the background and does not ask for you.',
  },
  {
    label: 'Watch',
    range: '50-69',
    score: 58,
    previous: 65,
    blurb: 'Slipping on one or more signals. Worth a look before it hardens.',
  },
  {
    label: 'Intervene',
    range: '0-49',
    score: 41,
    previous: 50,
    blurb: 'Needs an owner now, with the reason it is off course stated plainly.',
  },
]

export const healthValue = [
  {
    title: 'Two encodings that always agree',
    body: 'Arc length and colour say the same thing, so the read survives greyscale, colour blindness and a 16px table row.',
  },
  {
    title: 'It carries memory',
    body: 'Last period stays on the dial as an inner tick, so direction is visible without opening a chart.',
  },
  {
    title: 'It travels',
    body: 'The same dial holds its meaning in a table row, a collapsed rail, a printed board pack and an emailed PDF.',
  },
]

export const attentionPillars = [
  {
    title: 'See what needs attention',
    body: 'KeyTurn Health brings forward blocked, overdue, at-risk and consequential work while healthy progress stays in the background. Leaders stop inspecting hundreds of tasks to find the problem.',
  },
  {
    title: 'Know your next turn',
    body: 'My Turn prioritises each person’s questions and actions by what they unlock for the opening, not only by the nearest due date - including department work they can claim.',
  },
  {
    title: 'Be notified when it changes for you',
    body: 'KeyTurn understands who owns the work, what depends on it and whether a change affects opening. It alerts the people who need to act and leaves everyone else to keep moving.',
  },
]

export const myTurnQueue = [
  {
    unlocks: 'Releases online distribution',
    task: 'Confirm room type names for Marchmont House',
    band: 'Intervene' as const,
  },
  {
    unlocks: 'Requested in this morning’s meeting',
    task: 'Answer: which entity holds the liquor licence?',
    band: 'Watch' as const,
  },
  {
    unlocks: 'Blocks four downstream tasks',
    task: 'Approve the F&B opening stock order',
    band: 'Watch' as const,
  },
  {
    unlocks: 'Unclaimed in your department',
    task: 'Nominate the pre-opening training lead',
    band: 'Healthy' as const,
  },
]

export const meetingSteps = [
  {
    step: 'Before',
    title: 'An instant agenda for the people in the room',
    body: 'Tick who is present and KeyTurn brings forward what those attendees own, the open questions they can answer, the blocked work they can release and the decisions that now threaten opening.',
  },
  {
    step: 'During',
    title: 'Record in KeyTurn, or bring your own transcript',
    body: 'Use the built-in recorder or upload the transcript from the tool your team already uses. Nobody in the room takes minutes.',
  },
  {
    step: 'After',
    title: 'The discussion is mapped against the live opening',
    body: 'KeyTurn identifies new tasks, updates to existing ones, changed dates and owners, new and cleared blockers, questions, answers, decisions and referenced evidence.',
  },
  {
    step: 'One click',
    title: 'Review, then apply',
    body: 'A concise summary of proposed changes, each linked back to the moment in the discussion it came from. Approve and the opening plan updates.',
  },
]

export const meetingOutcomes = [
  { kind: 'New task', detail: 'Re-scope licence application with local counsel', tone: 'clay' as const },
  { kind: 'Date change', detail: 'Liquor licence granted → 14 Nov (crosses opening)', tone: 'clay' as const },
  { kind: 'Blocker cleared', detail: 'Room type names confirmed by brand', tone: 'sage' as const },
  { kind: 'Decision', detail: 'Soft-open F&B at reduced covers', tone: 'sand' as const },
  { kind: 'Question routed', detail: 'Which entity holds the licence? → Legal', tone: 'sand' as const },
]

export const workbookSteps = [
  'Upload the source workbook, whether it holds five tabs or 58.',
  'KeyTurn maps it into proposed tasks, questions, owners, milestones and dependencies - preserving the source sheet, cell and original wording.',
  'A person reviews the proposed structure before anything enters the plan.',
  'Unanswered questions are routed to the people most likely to resolve them, then chased automatically based on what the answer is blocking.',
  'Answers, decisions and completed work are written back to the mapped workbook fields.',
  'KeyTurn regenerates the completed workbook, ready to return to the vendor, brand or implementation partner.',
]

export const consequenceRows = [
  { task: 'Liquor licence granted', movement: '+9 days', crosses: true },
  { task: 'Bar staff training', movement: '+9 days', crosses: true },
  { task: 'F&B opening stock delivery', movement: '+6 days', crosses: false },
  { task: 'Trade press preview evening', movement: '+9 days', crosses: true },
  { task: 'POS menu configuration', movement: '+4 days', crosses: false },
]

export const supportingControl = [
  {
    title: 'Opening Budget',
    body: 'See allocated, committed and actual spend against the dates that matter, rolled up by milestone, category or department.',
  },
  {
    title: 'Questions, decisions and evidence',
    body: 'Keep the answer, the decision and the evidence with the work they changed - and with the audience they were shared with.',
  },
  {
    title: 'Files and version history',
    body: 'Every opening file current, controlled and connected to its context, including withdrawal history.',
  },
  {
    title: 'Role-aware collaboration',
    body: 'Operators, owners, consultants and vendors work in one environment with access shaped around each person’s role.',
  },
  {
    title: 'Latest Movement and search',
    body: 'See what changed, why it changed and where to act next, across every opening and task.',
  },
]
